import React from 'react';

import mixpanel from '~/components/CmsMain/mixpanel';
import type { CommunicationAttachmentChannel } from '~/components/communications/utils';
import { MIXPANEL_EVENTS } from '~/pocs/mixpanel';

import { FileIcon } from '../icons';
import TranslatedFileIcon from '../icons/TranslatedFileIcon';
import InlineIconButton from '../InlineIconButton';
import type { ClaimPolymorph } from '../types/claim-types';

interface GenericViewDocumentButtonProps {
  isDisabled?: boolean;
  isTranslated: boolean;
  fileUrl: string;
  translatedFileUrl?: string;
  onTranslatedFileClick?: () => void;
}

export const GenericViewDocumentButton: React.FC<GenericViewDocumentButtonProps> = ({
  isDisabled,
  isTranslated,
  fileUrl,
  translatedFileUrl,
  onTranslatedFileClick,
}) => {
  const handleTranslatedFileClick = () => {
    if (onTranslatedFileClick) {
      onTranslatedFileClick();
    }
  };

  return (
    <>
      <InlineIconButton
        tooltipTitle={
          isDisabled ? 'Document is stll uploading...' : isTranslated ? 'View original document' : 'View document'
        }
        icon={FileIcon}
        ignorePermissions
        disabled={isDisabled}
        href={fileUrl}
        target="_blank"
        rel="noreferrer"
      />
      {isTranslated && (
        <InlineIconButton
          tooltipTitle={isDisabled ? 'Document is still uploading...' : 'View translated document'}
          icon={TranslatedFileIcon}
          ignorePermissions
          disabled={isDisabled}
          href={translatedFileUrl}
          target="_blank"
          rel="noreferrer"
          onClick={handleTranslatedFileClick}
        />
      )}
    </>
  );
};
export interface ViewDocumentButtonProps {
  document: NonNullable<ClaimPolymorph['documents']>[number];
}

const ViewDocumentButton: React.FC<ViewDocumentButtonProps> = ({ document }) => {
  const handleTranslatedFileClick = () => {
    mixpanel.track(MIXPANEL_EVENTS.DOCUMENT_TRANSLATION_FILE_ACCESSED, {
      document_type: document.type,
      document_id: document.id,
      claim_id: document.claim_id,
    });
  };

  // For uploading documents, use the stored_file_id route instead of document_id route
  const isUploadingDocument = document.is_uploading && 'stored_file_id' in document && document.stored_file_id;
  const fileUrl = isUploadingDocument
    ? `/api/v1/claims/${document.claim_id}/uploading_document_stored_file/${document.stored_file_id}`
    : `/api/v1/claims/${document.claim_id}/documents/${document.id}`;
  const translatedFileUrl = isUploadingDocument
    ? undefined // Uploading documents don't have translations
    : `/api/v1/claims/${document.claim_id}/documents/${document.id}/translation`;

  return (
    <GenericViewDocumentButton
      isDisabled={document.is_uploading && !isUploadingDocument}
      isTranslated={Boolean(document.is_translated) && !isUploadingDocument}
      fileUrl={fileUrl}
      translatedFileUrl={translatedFileUrl}
      onTranslatedFileClick={handleTranslatedFileClick}
    />
  );
};

export default ViewDocumentButton;

export interface ViewCommunicationAttachmentButtonProps {
  attachment: {
    id: number;
    communication_id: number;
    is_translated: boolean;
  };
  channel: CommunicationAttachmentChannel;
}

export const ViewCommunicationAttachmentButton: React.FC<ViewCommunicationAttachmentButtonProps> = ({
  attachment,
  channel,
}) => {
  const handleTranslatedFileClick = () => {
    mixpanel.track(MIXPANEL_EVENTS.DOCUMENT_TRANSLATION_FILE_ACCESSED, {
      attachment_id: attachment.id,
      communication_id: attachment.communication_id,
      is_communication_attachment: true,
    });
  };

  const attachmentUrl = `/api/v1/communications/${channel}/${attachment.communication_id}/attachments/${attachment.id}`;
  // `emails` is the only channel with an attachment `/translation` route registered under
  // `/api/v1/communications/` — on the others the icon would open a raw 404 in a new tab.
  const hasTranslationRoute = channel === 'emails';

  return (
    <GenericViewDocumentButton
      isTranslated={attachment.is_translated && hasTranslationRoute}
      fileUrl={`${attachmentUrl}/stored_file`}
      translatedFileUrl={hasTranslationRoute ? `${attachmentUrl}/translation` : undefined}
      onTranslatedFileClick={handleTranslatedFileClick}
    />
  );
};
